import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Decimal from "decimal.js";

// 扩展 dayjs 插件
dayjs.extend(utc);
dayjs.extend(timezone);
/**
 * 加
 */
export const addNum = (num1: number, num2: number) => {
  return Decimal(num1).plus(Decimal(num2)).toNumber();
};
/**
 * 减
 */
export const subNum = (num1: number, num2: number) => {
  return Decimal(num1).sub(Decimal(num2)).toNumber();
};
/**
 * 乘
 */
export const mulNum = (num1: number, num2: number) => {
  return Decimal(num1).mul(Decimal(num2)).toNumber();
};
/**
 * 除
 */
export const divNum = (num1: number, num2: number) => {
  return Decimal(num1).div(Decimal(num2)).toNumber();
};
/**
 * 格式化日期
 */
export const formatDate = (
  date: string | Date,
  format = "YYYY-MM-DD HH:mm:ss"
) => {
  if (!date) return "--";

  return dayjs(date).format(format);
};
/**
 * 根据传入值 获取n天前的日期
 * value: 7 30 ..
 * type: day month
 */
export const getDateRange = (
  value: number,
  type: any,
  format = "YYYY-MM-DD HH:mm:ss"
) => {
  let startDate = dayjs();
  const endDate = dayjs().format(format);
  startDate = startDate.subtract(value, type);
  return [startDate.format(format), endDate];
};

/**
 * 禁用今日之后的日期（用于 el-date-picker 的 disabled-date 属性）
 * @param time 日期对象
 * @returns 如果是未来日期则返回 true（禁用）
 */
export const disabledFutureDate = (time: Date): boolean => {
  return time.getTime() > Date.now();
};

/**
 * 禁用今日之前的日期（用于 el-date-picker 的 disabled-date 属性）
 * @param time 日期对象
 * @returns 如果是过去日期则返回 true（禁用）
 */
export const disabledPastDate = (time: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return time.getTime() < today.getTime();
};

/**
 * 生成唯一ID
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * 深拷贝对象
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * 防抖函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * 节流函数
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * 存储工具
 */
export const storage = {
  get: (key: string) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },
  set: (key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore
    }
  },
  remove: (key: string) => {
    localStorage.removeItem(key);
  },
  clear: () => {
    localStorage.clear();
  },
};

/**
 * URL参数解析
 */
export const parseQuery = (search: string) => {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  for (const [key, value] of params) {
    result[key] = value;
  }
  return result;
};

/**
 * 文件大小格式化
 */
export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * 格式化数字
 * @param number 要格式化的数字（支持字符串/数值）
 * @param prefix 货币符号 $
 * @param decimals 保留小数位数（默认2）
 * @param dec_point 小数点符号（默认'.'）
 * @param thousands_sep 千分位符号（默认','）
 * @param roundtag 舍入方式：'ceil'向上取, 'floor'向下取, 'round'四舍五入（默认'floor'）
 * @returns 格式化后的字符串（无法解析时返回空字符串）
 */
export const formatNumber = (
  input: string | number,
  prefix: string = "",
  decimals: number = 2,
  decPoint: string = ".",
  thousandsSep: string = ",",
  roundingMode: "ceil" | "floor" | "round" = "floor"
): string => {
  // 清理和解析输入
  const numStr = String(input)
    .replace(/[^\d.Ee+-]/g, "")
    .replace(/,/g, "");
  const num = parseFloat(numStr);

  // 处理无效输入
  if (isNaN(num)) return "";

  // 处理符号
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // 计算舍入因子
  const factor = 10 ** decimals;

  // 根据舍入模式处理
  let roundedNum: number;
  switch (roundingMode) {
    case "ceil":
      roundedNum = isNegative
        ? -Math.floor(absNum * factor) / factor
        : Math.ceil(absNum * factor) / factor;
      break;
    case "floor":
      roundedNum = isNegative
        ? -Math.ceil(absNum * factor) / factor
        : Math.floor(absNum * factor) / factor;
      break;
    case "round":
    default:
      roundedNum = Math.round(num * factor) / factor;
  }

  // 格式化为固定小数
  const fixedNum = Math.abs(roundedNum).toFixed(decimals);
  const [intPart, decPart] = fixedNum.split(".");

  // 添加千分位分隔符
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);

  // 构建小数部分
  const formattedDec =
    decimals > 0 ? decPoint + (decPart || "").padEnd(decimals, "0") : "";

  // 构建完整结果
  const sign = isNegative ? "-" : "";
  const currencyPart = prefix ? prefix : "";
  // const currencyPart = prefix ? prefix + (sign ? ' ' : '') : '';

  return sign + currencyPart + formattedInt + formattedDec;
};

export * from "./constants";
export * from "./types";

/**
 * 动态设置页面favicon
 * @param iconUrl favicon的URL地址
 */
export function setFavicon(iconUrl: string) {
  if (typeof window === "undefined") return;

  try {
    // 移除现有的favicon
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach((favicon) => favicon.remove());

    // 创建新的favicon link元素
    const faviconLink = document.createElement("link");
    faviconLink.rel = "icon";
    faviconLink.type = "image/x-icon";
    faviconLink.href = iconUrl;

    // 添加到head中
    document.head.appendChild(faviconLink);
  } catch (error) {}
}

/**
 * 获取当前页面的favicon URL
 */
export function getCurrentFavicon(): string | null {
  if (typeof window === "undefined") return null;

  const faviconLink = document.querySelector(
    'link[rel*="icon"]'
  ) as HTMLLinkElement;
  return faviconLink ? faviconLink.href : null;
}

/**
 * 重置favicon为默认图标
 * @param defaultIconUrl 默认favicon的URL地址
 */
export function resetFavicon(defaultIconUrl: string = "/vite.svg") {
  setFavicon(defaultIconUrl);
}

/**
 * 国际化国家列表格式化工具
 * 用于格式化国家列表显示，支持多语言切换
 */
export interface CountryItem {
  id?: number;
  name:
    | string
    | {
        zh?: string; // 兼容旧数据
        en?: string;
        "zh-CN"?: string;
        "zh-TW"?: string;
      };
  code?: string;
}

/**
 * 格式化国家列表显示（支持多语言，动态根据当前语言切换）
 * @param countries 国家列表数组
 * @param locale 当前语言环境
 * @param globalConfigStore 全局配置store，用于获取国家名称
 * @returns 格式化后的国家名称字符串
 */
export const formatCountriesForDisplay = (
  countries: CountryItem[],
  locale: string,
  globalConfigStore?: any
): string => {
  if (!countries || countries.length === 0) return "";
  // 获取当前语言类型，默认为中文
  const currentLang =
    locale.startsWith("zh-TW") || locale.startsWith("zh-HK")
      ? "zh-TW"
      : locale.startsWith("zh")
      ? "zh-CN"
      : "en";

  return countries
    .map((country) => {
      // 如果name是多语言对象
      if (typeof country.name === "object" && country.name !== null) {
        if (currentLang === "en") {
          return country.name.en || "";
        }
        if (currentLang === "zh-TW") {
          return (
            country.name["zh-TW"] ||
            country.name["zh-CN"] ||
            country.name.zh ||
            country.name.en ||
            ""
          );
        }
        // zh-CN
        return (
          country.name["zh-CN"] ||
          country.name.zh ||
          country.name["zh-TW"] ||
          country.name.en ||
          ""
        );
      }

      // 如果有国家代码，使用globalConfigStore获取对应语言的名称
      if (country.code && globalConfigStore) {
        return globalConfigStore.getCountryName(country.code, currentLang);
      }

      // 如果name是字符串，直接使用
      if (typeof country.name === "string") {
        return country.name;
      }

      return "";
    })
    .filter(Boolean) // 过滤掉空值
    .join("，");
};

/**
 * 创建响应式的国家列表格式化函数
 * @param locale 响应式的语言环境
 * @param globalConfigStore 全局配置store
 * @returns 计算属性函数
 */
export const createCountriesFormatter = (
  locale: any,
  globalConfigStore?: any
) => {
  return (countries: CountryItem[]): string => {
    return formatCountriesForDisplay(
      countries,
      locale.value,
      globalConfigStore
    );
  };
};
/**
 * 根据国家代码获取国家名称（支持国际化）
 * @param countryCode 国家代码，如 'CN', 'US' 等
 * @param countryOptions 国家选项列表，包含 value 和 label 字段
 * @param locale 当前语言环境，如 'zh-CN', 'en-US'
 * @returns 对应的国家名称，如果找不到则返回原始国家代码
 */
export const getCountryName = (
  countryCode: string,
  countryOptions: Array<{
    value: string;
    label: string;
    labelEn?: string;
    labelZh?: string;
  }>,
  locale: string = "en"
): string => {
  if (!countryCode) return "--";

  const country = countryOptions.find((c) => c.value === countryCode);
  if (!country) return countryCode;

  // 根据语言环境返回对应的标签
  if (locale.startsWith("en")) {
    return country.labelEn || country.label || countryCode;
  } else {
    return country.labelZh || country.label || countryCode;
  }
};

/**
 * 输入框限制
 * @param value
 * @returns
 */
export const filterAmountInput = (value: any) => {
  let result = value;

  // 1. 过滤非数字和小数点
  result = result.replace(/[^\d.]/g, "");

  // 2. 处理连续小数点
  const dotCount = (result.match(/\./g) || []).length;
  if (dotCount > 1) {
    result = result.slice(0, result.lastIndexOf("."));
  }

  // 3. 处理小数点开头
  if (result.startsWith(".")) {
    result = "0.";
  }

  // 4. 处理整数部分0开头
  if (/^0[^.]/.test(result)) {
    result = result.substring(1);
  }

  // 5. 限制小数点后两位
  const parts = result.split(".");
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, 2);
    result = parts[0] + "." + parts[1];
  }

  return result;
};

/**
 * 存储Cookie
 * @param cookieName cookie名
 * @param cookieValue cookie值
 * @param cookieHours 存储时间（小时）
 */
export const saveCookie = (
  cookieName: string,
  cookieValue: string,
  cookieHours: number
): void => {
  const d = new Date();
  d.setTime(d.getTime() + cookieHours * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  const encodedValue = encodeURIComponent(cookieValue);
  document.cookie = `${cookieName}=${encodedValue};${expires};path=/`;
};

/**
 * 获取Cookie
 * @param cookieName cookie名
 * @returns cookie值，如果不存在则返回null
 */
export const getCookie = (cookieName: string): string | null => {
  const name = `${cookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArr = decodedCookie.split("; ");

  for (let i = 0; i < cookieArr.length; i++) {
    let cookie = cookieArr[i];
    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return null;
};

/**
 * 获取目标时区
 * @param targetTimezone 指定的目标时区，如果为空则使用浏览器本地时区
 * @returns 目标时区字符串
 */
export const getTargetTimezone = (targetTimezone?: string): string => {
  if (targetTimezone) {
    return targetTimezone;
  }
  // 使用浏览器本地时区
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * 转换时间字段为本地时区
 * @param value 时间值（通常是UTC时间字符串）
 * @param targetTimezone 目标时区，如果为空则使用浏览器本地时区
 * @param timeFormat 时间格式，默认为 'YYYY-MM-DD HH:mm:ss'
 * @returns 转换后的时间字符串
 */
export const convertTimeToLocal = (
  value: any,
  targetTimezone?: string,
  timeFormat: string = "YYYY-MM-DD HH:mm:ss"
): string => {
  if (!value) {
    return value;
  }

  try {
    // 假设后端返回的是 UTC 时间
    const utcTime = dayjs.utc(value);
    if (!utcTime.isValid()) {
      return value;
    }

    // 转换为目标时区
    const timezone = getTargetTimezone(targetTimezone);
    const localTime = utcTime.tz(timezone);

    return localTime.format(timeFormat);
  } catch (error) {
    console.warn(`时间转换失败: ${value}`, error);
    return value;
  }
};

/**
 * 转换时间字段为本地时区（简化版本，使用默认格式）
 * @param value 时间值（通常是UTC时间字符串）
 * @returns 转换后的时间字符串
 */
export const convertTime = (value: any): string => {
  return convertTimeToLocal(value);
};

/**
 * CSV 导出接口定义
 */
export interface CSVExportColumn {
  prop: string;
  label: string;
  formatter?: (value: any, row: any) => string; // 自定义格式化函数
}

/**
 * 前端导出 CSV 文件
 * @param columns 列定义数组，包含 prop 和 label
 * @param data 要导出的数据数组
 * @param filename 文件名（不含扩展名），默认为 'export'
 * @param options 额外配置选项
 */
export interface CSVExportOptions {
  /** 是否包含 BOM（用于 Excel 正确识别 UTF-8），默认 true */
  includeBOM?: boolean;
}

export const exportToCSV = (
  columns: CSVExportColumn[],
  data: any[],
  filename: string = "export",
  options: CSVExportOptions = {}
): void => {
  const { includeBOM = true } = options;

  // 转义 CSV 字段值
  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) {
      return "";
    }
    const str = String(value);
    // 如果包含逗号、引号或换行符，需要用引号包裹并转义内部引号
    if (
      str.includes(",") ||
      str.includes('"') ||
      str.includes("\n") ||
      str.includes("\r")
    ) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // 生成表头行
  const headerRow = columns.map((col) => escapeCSVValue(col.label)).join(",");

  // 生成数据行
  const dataRows = data.map((row) => {
    return columns
      .map((col) => {
        let value = row[col.prop];
        // 如果有自定义格式化函数，使用格式化函数
        if (col.formatter) {
          value = col.formatter(value, row);
        }
        return escapeCSVValue(value);
      })
      .join(",");
  });

  // 组合 CSV 内容
  const csvContent = [headerRow, ...dataRows].join("\n");

  // 添加 BOM（用于 Excel 正确识别 UTF-8）
  const BOM = "\uFEFF";
  const finalContent = includeBOM ? BOM + csvContent : csvContent;

  // 创建 Blob 并下载
  const blob = new Blob([finalContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.csv`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
// 将数据转成树状结构
export const toTreeData = (data: any) => {
  let arr = JSON.parse(JSON.stringify(data));
  const listChildren = (obj: any, filter: any) => {
    [arr, obj.children] = arr.reduce(
      (res: any, val: any) => {
        if (filter(val)) {
          res[1].push(val);
        } else {
          res[0].push(val);
        }
        return res;
      },
      [[], []]
    );
    obj.children.forEach((val: any) => {
      if (arr.length) {
        listChildren(val, (obj: any) => obj.parent_id == val.id);
      }
    });
  };
  const tree = { children: {} };
  listChildren(
    tree,
    (val: any) => arr.findIndex((i: any) => i.id == val.parent_id) === -1
  );
  return tree.children;
};
